import React, {useEffect, useMemo, useState} from "react";
import Modal from "react-modal";
import NotificationManager from "react-notifications/lib/NotificationManager";
import {Button, FormControlLabel, IconButton, Switch, ThemeProvider, Tooltip} from "@mui/material";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ClearIcon from '@mui/icons-material/Clear';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {buttonTheme, switchTheme} from "../utils/muiStyling";
import {
    MuiAutocompleteForZenGroupsWithoutCreateGroupOption,
    MuiAutocompleteNonGroupOptions,
    MuiCloseIconButton,
    MuiIconWithTooltip
} from "./muiComponents";

/* ================================================================== */
/* v2 whitelist pattern engine                                         */
/* Mirrors the agent's v2 regex implementation:                        */
/* ^ $ . * + ? {n} {n,} {n,m} [abc] [^abc] [a-z]                       */
/* \d \D \s \S \p \P \q \Q \w \W, backslash-escaped literals           */
/* ================================================================== */

const META_CODES = new Set(["d", "D", "s", "S", "p", "P", "q", "Q", "w", "W"]);

const META_LABEL = {
    d: "digit", D: "non-digit",
    s: "whitespace", S: "non-whitespace",
    p: "path slash (/ or \\)", P: "non-slash",
    q: "quote (' or \")", Q: "non-quote",
    w: "alphanumeric/_", W: "non-alphanumeric",
};

function isMetaMatch(code, ch) {
    switch (code) {
        case "d": return ch >= "0" && ch <= "9";
        case "D": return !(ch >= "0" && ch <= "9");
        case "s": return ch === " " || ch === "\t" || ch === "\f" || ch === "\r" || ch === "\n" || ch === "\v";
        case "S": return !isMetaMatch("s", ch);
        case "p": return ch === "/" || ch === "\\";
        case "P": return !isMetaMatch("p", ch);
        case "q": return ch === "'" || ch === '"';
        case "Q": return !isMetaMatch("q", ch);
        case "w": return /[a-zA-Z0-9_]/.test(ch);
        case "W": return !/[a-zA-Z0-9_]/.test(ch);
        default: return false;
    }
}

const quantifiable = (tok) =>
    tok && (tok.type === "char" || tok.type === "dot" || tok.type === "meta" || tok.type === "class");

/** Tokenize a v2 pattern. Returns { tokens, errors }. errors: [{pos, msg}] */
export function tokenizeV2(pattern) {
    const tokens = [];
    const errors = [];
    const err = (pos, msg) => errors.push({pos, msg});
    let i = 0;

    const attachQuant = (pos, display, quant) => {
        const prev = tokens[tokens.length - 1];
        if (!quantifiable(prev)) {
            err(pos, `Quantifier '${display}' has nothing to repeat — it must follow a character, '.', a character class, or an escape like \\d or \\P.`);
        } else if (prev.quant) {
            err(pos, `Two quantifiers in a row ('${prev.quantDisplay}' then '${display}'). A token can only have one quantifier.`);
        } else {
            prev.quant = quant;
            prev.quantDisplay = display;
        }
    };

    while (i < pattern.length) {
        const c = pattern[i];
        if (c === "^") {
            if (i !== 0) err(i, "Start anchor '^' is only valid at the very beginning of the pattern.");
            else tokens.push({type: "begin", pos: i});
            i++;
        } else if (c === "$") {
            if (i !== pattern.length - 1) err(i, "End anchor '$' is only valid at the very end of the pattern.");
            else tokens.push({type: "end", pos: i});
            i++;
        } else if (c === ".") {
            tokens.push({type: "dot", pos: i});
            i++;
        } else if (c === "*") {
            attachQuant(i, "*", {min: 0, max: Infinity, greedy: true});
            i++;
        } else if (c === "+") {
            attachQuant(i, "+", {min: 1, max: Infinity, greedy: true});
            i++;
        } else if (c === "?") {
            attachQuant(i, "?", {min: 0, max: 1, greedy: false});
            i++;
        } else if (c === "{") {
            const close = pattern.indexOf("}", i);
            if (close === -1) {
                err(i, "Unclosed '{' — quantifiers must look like {5}, {5,} or {5,9}.");
                i++;
                continue;
            }
            const body = pattern.slice(i + 1, close);
            const m = /^(\d+)(,(\d*))?$/.exec(body);
            if (!m) {
                err(i, `Invalid quantifier '{${body}}' — use {n}, {n,} or {n,m} with plain digits.`);
            } else {
                const min = parseInt(m[1], 10);
                const max = m[2] === undefined ? min : (m[3] === "" || m[3] === undefined ? Infinity : parseInt(m[3], 10));
                if (max < min) err(i, `Invalid range '{${body}}' — the minimum (${min}) is greater than the maximum (${max}).`);
                else attachQuant(i, `{${body}}`, {min, max, greedy: true});
            }
            i = close + 1;
        } else if (c === "[") {
            const start = i;
            i++;
            let negated = false;
            if (pattern[i] === "^") { negated = true; i++; }
            const items = [];
            let closed = false;
            while (i < pattern.length) {
                const cc = pattern[i];
                if (cc === "]") { closed = true; i++; break; }
                if (cc === "\\") {
                    const nx = pattern[i + 1];
                    if (nx === undefined) { err(i, "Pattern ends with a lone '\\' inside a character class."); i++; break; }
                    if (META_CODES.has(nx)) items.push({kind: "meta", code: nx});
                    else items.push({kind: "char", ch: nx});
                    i += 2;
                } else if (pattern[i + 1] === "-" && pattern[i + 2] !== undefined && pattern[i + 2] !== "]") {
                    const from = cc;
                    const to = pattern[i + 2] === "\\" ? pattern[i + 3] : pattern[i + 2];
                    const toLen = pattern[i + 2] === "\\" ? 2 : 1;
                    if (to === undefined) { err(i, "Character range is missing its end character."); i += 2; continue; }
                    if (from.charCodeAt(0) > to.charCodeAt(0)) err(i, `Invalid range '${from}-${to}' — the range runs backwards.`);
                    else items.push({kind: "range", from, to});
                    i += 2 + toLen;
                } else {
                    items.push({kind: "char", ch: cc});
                    i++;
                }
            }
            if (!closed) err(start, "Unclosed '[' — character classes must end with ']'.");
            else if (items.length === 0) err(start, "Empty character class '[]' matches nothing.");
            tokens.push({type: "class", negated, items, pos: start});
        } else if (c === "\\") {
            const nx = pattern[i + 1];
            if (nx === undefined) {
                err(i, "Pattern ends with a lone '\\'. Escape it as '\\\\' to match a literal backslash.");
                i++;
            } else if (META_CODES.has(nx)) {
                tokens.push({type: "meta", code: nx, pos: i});
                i += 2;
            } else {
                tokens.push({type: "char", ch: nx, escaped: true, pos: i});
                i += 2;
            }
        } else {
            tokens.push({type: "char", ch: c, pos: i});
            i++;
        }
    }
    return {tokens, errors};
}

function classItemMatch(item, ch, ci) {
    if (item.kind === "meta") return isMetaMatch(item.code, ch);
    if (item.kind === "char") return ci ? item.ch.toLowerCase() === ch.toLowerCase() : item.ch === ch;
    const inRange = (x) => x >= item.from && x <= item.to;
    if (inRange(ch)) return true;
    if (!ci) return false;
    return inRange(ch.toLowerCase()) || inRange(ch.toUpperCase());
}

function tokenMatchesChar(tok, ch, ci) {
    switch (tok.type) {
        case "char": return ci ? tok.ch.toLowerCase() === ch.toLowerCase() : tok.ch === ch;
        case "dot": return true;
        case "meta": return isMetaMatch(tok.code, ch);
        case "class": {
            const any = tok.items.some((it) => classItemMatch(it, ch, ci));
            return tok.negated ? !any : any;
        }
        default: return false;
    }
}

function matchHere(toks, ti, str, si, ci, rec) {
    if (ti === toks.length) return si;
    const tok = toks[ti];
    if (tok.type === "end") {
        if (si === str.length) return si;
        if (rec) rec.fail(si, tok, "end-anchor");
        return -1;
    }
    const q = tok.quant || {min: 1, max: 1, greedy: true};
    if (q.greedy) {
        let count = 0;
        while (count < q.max && si + count < str.length && tokenMatchesChar(tok, str[si + count], ci)) count++;
        if (count < q.min) {
            if (rec) rec.fail(si + count, tok, si + count >= str.length ? "ended" : "mismatch");
            return -1;
        }
        for (let k = count; k >= q.min; k--) {
            const r = matchHere(toks, ti + 1, str, si + k, ci, rec);
            if (r !== -1) return r;
        }
        return -1;
    }
    let k = 0;
    for (;;) {
        if (k >= q.min) {
            const r = matchHere(toks, ti + 1, str, si + k, ci, rec);
            if (r !== -1) return r;
        }
        if (k >= q.max) return -1;
        if (si + k < str.length && tokenMatchesChar(tok, str[si + k], ci)) k++;
        else {
            if (k < q.min && rec) rec.fail(si + k, tok, si + k >= str.length ? "ended" : "mismatch");
            return -1;
        }
    }
}

/** Match tokens against a string. Returns {start, end} or null. */
export function matchV2(tokens, str, ci) {
    const anchored = tokens.length > 0 && tokens[0].type === "begin";
    const core = anchored ? tokens.slice(1) : tokens;
    if (anchored) {
        const r = matchHere(core, 0, str, 0, ci);
        return r !== -1 ? {start: 0, end: r} : null;
    }
    for (let s = 0; s <= str.length; s++) {
        const r = matchHere(core, 0, str, s, ci);
        if (r !== -1) return {start: s, end: r};
    }
    return null;
}

/** Deepest failure point for explaining why a string was NOT matched. */
function diagnoseV2(tokens, str, ci) {
    const anchored = tokens.length > 0 && tokens[0].type === "begin";
    const core = anchored ? tokens.slice(1) : tokens;
    let best = null;
    const recFor = (start) => ({
        fail(pos, tok, kind) {
            const prog = pos - start;
            if (!best || prog > best.prog || (prog === best.prog && pos > best.pos)) {
                best = {pos, start, prog, tok, kind};
            }
        },
    });
    if (anchored) matchHere(core, 0, str, 0, ci, recFor(0));
    else for (let s = 0; s <= str.length; s++) matchHere(core, 0, str, s, ci, recFor(s));
    return best;
}

function tokenDisplayText(tok) {
    switch (tok.type) {
        case "begin": return "^";
        case "end": return "$";
        case "char": return tok.escaped ? "\\" + tok.ch : tok.ch;
        case "dot": return ".";
        case "meta": return "\\" + tok.code;
        case "class": {
            const inner = tok.items
                .map((it) => (it.kind === "range" ? `${it.from}-${it.to}` : it.kind === "meta" ? "\\" + it.code : it.ch))
                .join("");
            return `[${tok.negated ? "^" : ""}${inner}]`;
        }
        default: return "?";
    }
}

function describeExpected(tok) {
    if (tok.type === "char" && !tok.quantDisplay) return `the literal character '${tok.ch}'`;
    const disp = tokenDisplayText(tok) + (tok.quantDisplay || "");
    let meaning;
    switch (tok.type) {
        case "char": meaning = `literal '${tok.ch}'`; break;
        case "dot": meaning = "any character"; break;
        case "meta": meaning = META_LABEL[tok.code]; break;
        case "class": meaning = tok.negated ? "any character not in the listed set" : "one of the listed characters"; break;
        default: meaning = "";
    }
    const more = tok.quant && tok.quant.min > 1 ? `, at least ${tok.quant.min} of them` : "";
    return `'${disp}' (${meaning}${more})`;
}

function blockedMessage(diag, str, anchoredStart) {
    const at = diag.pos;
    const prefix = !anchoredStart && diag.start > 0 ? `Best attempt started at position ${diag.start}. ` : "";
    if (diag.kind === "end-anchor") {
        const rest = str.slice(at);
        const preview = rest.length > 24 ? rest.slice(0, 24) + "…" : rest;
        return `${prefix}Everything up to position ${at} matched, but the rule requires the input to end there — it continues with '${preview}'.`;
    }
    if (diag.kind === "ended") {
        return `${prefix}The input ended after the highlighted portion, but the rule still expects ${describeExpected(diag.tok)}.`;
    }
    if (at === diag.start) {
        return `${prefix}No characters matched: the rule expects ${describeExpected(diag.tok)}${anchoredStart ? " at the very start" : " here"}, but found '${str[at]}'.`;
    }
    return `${prefix}The highlighted portion matched, then the rule expected ${describeExpected(diag.tok)} at position ${at} but the input has '${str[at]}' there.`;
}

/* ------------------------------------------------------------------ */
/* Example generation: derive a realistic matching string and a        */
/* non-matching string from the pattern, to showcase what the built    */
/* rule would allow and block.                                         */
/* ------------------------------------------------------------------ */

const SAMPLE_PALETTE = "sampledir123 _-.\\/\"'";

function samplePaletteChar(tok, offset) {
    for (let t = 0; t < SAMPLE_PALETTE.length; t++) {
        const cand = SAMPLE_PALETTE[(offset + t) % SAMPLE_PALETTE.length];
        if (tokenMatchesChar(tok, cand, true)) return cand;
    }
    // fallback: scan printable ASCII for anything the token accepts
    for (let c = 32; c < 127; c++) {
        const ch = String.fromCharCode(c);
        if (tokenMatchesChar(tok, ch, true)) return ch;
    }
    return null;
}

/** Produce a representative string for one token (respecting its quantifier). */
function sampleRun(tok) {
    const q = tok.quant || {min: 1, max: 1};
    let len;
    if (q.max === Infinity) {
        const nice = tok.type === "meta" && tok.code === "d" ? 3 : tok.type === "meta" && tok.code === "s" ? 1 : 6;
        len = Math.max(q.min, nice);
    } else {
        len = q.min; // '?' and exact/range quantifiers demonstrate the minimum
    }
    if (len === 0) return "";
    if (tok.type === "char") return tok.ch.repeat(len);
    let out = "";
    for (let i = 0; i < len; i++) {
        const ch = samplePaletteChar(tok, i);
        if (ch === null) return null;
        out += ch;
    }
    return out;
}

/**
 * Generate {working, failing} example strings for a tokenized pattern.
 * working: a string the pattern matches (null if one couldn't be built).
 * failing: a string it does not match (null if seemingly everything matches).
 * In argsMode, unanchored sides get "before "/" after" context to showcase
 * the agent's implicit wildcards on argument patterns.
 */
export function generateV2Examples(tokens, opts) {
    const {argsMode = false, anchorStart = true, anchorEnd = true} = opts || {};
    const core = tokens.filter((t) => t.type !== "begin" && t.type !== "end");
    const parts = core.map(sampleRun);
    let working = parts.some((p) => p === null) ? null : parts.join("");
    if (working !== null && argsMode) {
        const decorated = (anchorStart ? "" : "before ") + working + (anchorEnd ? "" : " after");
        if (matchV2(tokens, decorated, true)) working = decorated;
    }
    if (working !== null && !matchV2(tokens, working, true)) working = null;

    const candidates = argsMode
        ? ["--other-flag value", "xyz", ""]
        : [(working || "sample") + ".bak", "c:\\other\\thing.txt", "x", ""];
    let failing = null;
    for (const c of candidates) {
        if (!matchV2(tokens, c, true)) {
            failing = c;
            break;
        }
    }
    return {working, failing};
}

/* ================================================================== */
/* Builder: friendly building blocks -> v2 pattern                     */
/* ================================================================== */

/** Escape user text so it matches literally in a v2 pattern. */
export function escapeV2Literal(text) {
    let out = "";
    for (const ch of text) {
        if (ch === "/" || ch === "\\") out += "\\p";
        else if ("^$.*+?{}[]".includes(ch)) out += "\\" + ch;
        else out += ch;
    }
    return out;
}

/**
 * Translate a raw value (a real path or argument string, e.g. from an incident)
 * into a v2 pattern that matches exactly that value and nothing else.
 * Slashes become \p, so the pattern matches the path with either slash direction.
 */
export function rawToExactV2Pattern(raw) {
    const trimmed = (raw || "").trim();
    if (trimmed.length === 0) return "";
    return "^" + escapeV2Literal(trimmed) + "$";
}

const SEGMENT_KINDS = [
    {kind: "exact", label: "Folder named exactly…", needsText: true, placeholder: "program files"},
    {kind: "starts", label: "Folder starting with…", needsText: true, placeholder: "ms"},
    {kind: "ends", label: "Folder ending with…", needsText: true, placeholder: "_v2"},
    {kind: "contains", label: "Folder containing…", needsText: true, placeholder: "vendor"},
    {kind: "anyone", label: "Any single folder", needsText: false},
    {kind: "anydeep", label: "Any folders (1+ levels deep)", needsText: false},
];

const FILE_KINDS = [
    {kind: "anyExt", label: "Any file with extension…", fields: ["ext"]},
    {kind: "exactFile", label: "File named exactly…", fields: ["name"]},
    {kind: "startsExt", label: "File starting with… + extension", fields: ["prefix", "ext"]},
    {kind: "anyFile", label: "Any file", fields: []},
];

const START_KINDS = [
    {kind: "drive", label: "Specific drive"},
    {kind: "anyDrive", label: "Any drive letter"},
    {kind: "custom", label: "Custom literal prefix"},
    {kind: "none", label: "None (starts at a slash)"},
];

function segmentBody(seg) {
    const esc = escapeV2Literal(seg.text || "");
    switch (seg.kind) {
        case "exact": return esc;
        case "starts": return esc + "\\P*";
        case "ends": return "\\P*" + esc;
        case "contains": return "\\P*" + esc + "\\P*";
        case "anyone": return "\\P+";
        case "anydeep": return ".+"; // '.' crosses slashes, so this spans 1+ folder levels
        default: return "";
    }
}

function fileBody(file) {
    const ext = escapeV2Literal((file.ext || "").replace(/^\./, ""));
    switch (file.kind) {
        case "exactFile": return escapeV2Literal(file.name || "");
        case "anyExt": return "\\P+\\." + ext;
        case "startsExt": return escapeV2Literal(file.prefix || "") + "\\P*\\." + ext;
        case "anyFile": return "\\P+";
        default: return "";
    }
}

function buildPattern(state) {
    let p = "^";
    switch (state.start.kind) {
        case "drive": p += escapeV2Literal((state.start.letter || "c").toLowerCase()) + ":"; break;
        case "anyDrive": p += "[a-zA-Z]:"; break;
        case "custom": p += escapeV2Literal(state.start.text || ""); break;
        default: break;
    }
    for (const seg of state.segments) p += "\\p" + segmentBody(seg);
    p += "\\p" + fileBody(state.file);
    p += "$";
    return p;
}

function builderProblems(state) {
    const problems = [];
    if (state.start.kind === "drive" && !/^[a-zA-Z]$/.test(state.start.letter || "")) {
        problems.push("Drive must be a single letter (a–z).");
    }
    if (state.start.kind === "custom" && !(state.start.text || "").trim()) {
        problems.push("Custom prefix is empty — enter the literal text the path must start with.");
    }
    state.segments.forEach((seg, i) => {
        const def = SEGMENT_KINDS.find((k) => k.kind === seg.kind);
        if (def && def.needsText && !(seg.text || "").trim()) {
            problems.push(`Folder step ${i + 1} ('${def.label.replace("…", "")}') needs text.`);
        }
    });
    const f = state.file;
    if ((f.kind === "anyExt" || f.kind === "startsExt") && !(f.ext || "").replace(/^\./, "").trim()) {
        problems.push("File extension is empty (e.g. exe, dll, ps1).");
    }
    if (f.kind === "exactFile" && !(f.name || "").trim()) problems.push("File name is empty.");
    if (f.kind === "startsExt" && !(f.prefix || "").trim()) problems.push("File name prefix is empty.");
    return problems;
}

/* ------------------------------------------------------------------ */
/* Reverse parsing: v2 pattern -> builder blocks                       */
/* Used to repopulate the builder when a field already has a pattern.  */
/* Only shapes the builders generate are recognized; anything else     */
/* returns null and the builder opens fresh with a notice.             */
/* ------------------------------------------------------------------ */

const isPlainChar = (t) => t.type === "char" && !t.quant;
const isMetaTok = (t, code, quantDisplay) =>
    t.type === "meta" && t.code === code &&
    (quantDisplay === undefined ? !t.quant : t.quantDisplay === quantDisplay);
const joinChars = (toks) => toks.map((t) => t.ch).join("");
const allPlainChars = (toks) => toks.length > 0 && toks.every(isPlainChar);

function parseStartGroup(g) {
    if (g.length === 2 && g[0].type === "class" && !g[0].quant && !g[0].negated && isPlainChar(g[1]) && g[1].ch === ":") {
        const items = g[0].items || [];
        const coversLetters = items.length === 2 &&
            items.every((it) => it.kind === "range") &&
            items.some((it) => it.from === "a" && it.to === "z") &&
            items.some((it) => it.from === "A" && it.to === "Z");
        return coversLetters ? {kind: "anyDrive", letter: "c", text: ""} : null;
    }
    if (g.length === 2 && isPlainChar(g[0]) && /^[a-zA-Z]$/.test(g[0].ch) && isPlainChar(g[1]) && g[1].ch === ":") {
        return {kind: "drive", letter: g[0].ch.toLowerCase(), text: ""};
    }
    if (allPlainChars(g)) return {kind: "custom", letter: "c", text: joinChars(g)};
    return null;
}

function parseSegmentGroup(g) {
    if (g.length === 1 && isMetaTok(g[0], "P", "+")) return {kind: "anyone", text: ""};
    if (g.length === 1 && g[0].type === "dot" && g[0].quantDisplay === "+") return {kind: "anydeep", text: ""};
    if (allPlainChars(g)) return {kind: "exact", text: joinChars(g)};
    if (g.length === 0) return null;
    const firstStar = isMetaTok(g[0], "P", "*");
    const lastStar = isMetaTok(g[g.length - 1], "P", "*");
    if (firstStar && lastStar && g.length > 2 && allPlainChars(g.slice(1, -1))) return {kind: "contains", text: joinChars(g.slice(1, -1))};
    if (firstStar && g.length > 1 && allPlainChars(g.slice(1))) return {kind: "ends", text: joinChars(g.slice(1))};
    if (lastStar && g.length > 1 && allPlainChars(g.slice(0, -1))) return {kind: "starts", text: joinChars(g.slice(0, -1))};
    return null;
}

function parseFileGroup(g) {
    if (g.length === 1 && isMetaTok(g[0], "P", "+")) return {kind: "anyFile", name: "", prefix: "", ext: ""};
    if (g.length >= 3 && isMetaTok(g[0], "P", "+") && isPlainChar(g[1]) && g[1].ch === "." && allPlainChars(g.slice(2))) {
        return {kind: "anyExt", name: "", prefix: "", ext: joinChars(g.slice(2))};
    }
    const pi = g.findIndex((t) => isMetaTok(t, "P", "*"));
    if (pi > 0 && g[pi + 1] && isPlainChar(g[pi + 1]) && g[pi + 1].ch === "." &&
        allPlainChars(g.slice(0, pi)) && allPlainChars(g.slice(pi + 2))) {
        return {kind: "startsExt", name: "", prefix: joinChars(g.slice(0, pi)), ext: joinChars(g.slice(pi + 2))};
    }
    if (allPlainChars(g)) return {kind: "exactFile", name: joinChars(g), prefix: "", ext: ""};
    return null;
}

/** Parse a builder-generated path pattern back into builder state, or null. */
export function parsePathPatternToBuilder(pattern) {
    const {tokens, errors} = tokenizeV2(pattern);
    if (errors.length > 0) return null;
    if (tokens.length < 2 || tokens[0].type !== "begin" || tokens[tokens.length - 1].type !== "end") return null;
    const core = tokens.slice(1, -1);
    const groups = [[]];
    for (const t of core) {
        if (isMetaTok(t, "p")) groups.push([]);
        else groups[groups.length - 1].push(t);
    }
    if (groups.length < 2) return null; // needs at least one '\p' before the file part
    let start;
    let gi;
    if (groups[0].length === 0) {
        // leading '\p's: one means "starts at a slash", more (e.g. UNC \\server) map to a
        // custom prefix of backslashes — regenerates to the semantically identical pattern
        let k = 1;
        while (k < groups.length - 1 && groups[k].length === 0) k++;
        start = k === 1 ? {kind: "none", letter: "c", text: ""} : {kind: "custom", letter: "c", text: "\\".repeat(k - 1)};
        gi = k;
    } else {
        start = parseStartGroup(groups[0]);
        if (!start) return null;
        gi = 1;
    }
    if (gi >= groups.length) return null;
    const segGroups = groups.slice(gi, groups.length - 1);
    const segments = [];
    for (const g of segGroups) {
        const s = parseSegmentGroup(g);
        if (!s) return null;
        segments.push(s);
    }
    const file = parseFileGroup(groups[groups.length - 1]);
    if (!file) return null;
    return {start, segments, file};
}

/* ------------------------------------------------------------------ */
/* Argument pattern building blocks                                    */
/* Arguments aren't paths, so they get their own vocabulary. Blocks    */
/* are concatenated left to right and the pattern is anchored, so it   */
/* must describe the ENTIRE argument string.                           */
/* ------------------------------------------------------------------ */

const ARG_KINDS = [
    {kind: "literal", label: "Exact text…", needsText: true, placeholder: "-flag value"},
    {kind: "anything", label: "Anything (any characters, or nothing)", needsText: false},
    {kind: "word", label: "A word (1+ non-space characters)", needsText: false},
    {kind: "number", label: "A number (optional +/- sign, then digits)", needsText: false},
    {kind: "space", label: "Whitespace (1+ spaces/tabs)", needsText: false},
    {kind: "quoted", label: "A quoted value (\"…\" or '…')", needsText: false},
];

function argBlockBody(b) {
    switch (b.kind) {
        case "literal": return escapeV2Literal(b.text || "");
        case "anything": return ".*";
        case "word": return "\\S+";
        case "number": return "[+-]?\\d+";
        case "space": return "\\s+";
        case "quoted": return "\\q\\Q+\\q";
        default: return "";
    }
}

/**
 * Split a real argument string into builder blocks: quoted sections and
 * space-separated chunks become exact-text blocks, separated by whitespace
 * blocks. The user can then generalize individual pieces (e.g. flip a chunk
 * to 'A number' or a quoted section to 'A quoted value').
 */
function splitRawArgsToBlocks(raw) {
    const s = (raw || "").trim();
    const blocks = [];
    let i = 0;
    while (i < s.length) {
        const c = s[i];
        if (c === " " || c === "\t") {
            while (i < s.length && (s[i] === " " || s[i] === "\t")) i++;
            blocks.push({kind: "space", text: ""});
        } else if (c === '"' || c === "'") {
            const close = s.indexOf(c, i + 1);
            if (close !== -1) {
                blocks.push({kind: "literal", text: s.slice(i, close + 1)});
                i = close + 1;
            } else {
                // unbalanced quote: treat the rest of the chunk as literal text
                let j = i;
                while (j < s.length && s[j] !== " " && s[j] !== "\t") j++;
                blocks.push({kind: "literal", text: s.slice(i, j)});
                i = j;
            }
        } else {
            let j = i;
            while (j < s.length && s[j] !== " " && s[j] !== "\t" && s[j] !== '"' && s[j] !== "'") j++;
            blocks.push({kind: "literal", text: s.slice(i, j)});
            i = j;
        }
    }
    return blocks;
}

function buildArgsPattern(blocks, anchorStart, anchorEnd) {
    return (anchorStart ? "^" : "") + blocks.map(argBlockBody).join("") + (anchorEnd ? "$" : "");
}

function argsProblems(blocks) {
    const problems = [];
    if (blocks.length === 0) problems.push("Add at least one block.");
    blocks.forEach((b, i) => {
        if (b.kind === "literal" && !(b.text || "").trim()) problems.push(`Block ${i + 1} ('Exact text') needs text.`);
    });
    return problems;
}

/**
 * Parse a builder-generated arguments pattern back into blocks and anchor
 * flags, or null. The agent implicitly wildcards the start and end of
 * argument patterns, so '^'/'$' are opt-in and tracked as flags here.
 */
export function parseArgsPatternToBuilder(pattern) {
    const {tokens, errors} = tokenizeV2(pattern);
    if (errors.length > 0) return null;
    if (tokens.length === 0) return null;
    const anchorStart = tokens[0].type === "begin";
    const anchorEnd = tokens[tokens.length - 1].type === "end";
    const core = tokens.slice(anchorStart ? 1 : 0, anchorEnd ? tokens.length - 1 : tokens.length);
    if (core.length === 0) return null;
    const blocks = [];
    let i = 0;
    while (i < core.length) {
        const t = core[i];
        if (isPlainChar(t) || isMetaTok(t, "p")) {
            // literal run; '\p' inside a literal came from an escaped slash in the user's text
            let text = "";
            while (i < core.length && (isPlainChar(core[i]) || isMetaTok(core[i], "p"))) {
                text += isPlainChar(core[i]) ? core[i].ch : "\\";
                i++;
            }
            blocks.push({kind: "literal", text});
        } else if (t.type === "dot" && t.quantDisplay === "*") {
            blocks.push({kind: "anything"});
            i++;
        } else if (isMetaTok(t, "S", "+")) {
            blocks.push({kind: "word"});
            i++;
        } else if (
            t.type === "class" && !t.negated && t.quantDisplay === "?" &&
            Array.isArray(t.items) && t.items.length === 2 &&
            t.items.every((it) => it.kind === "char") &&
            t.items.some((it) => it.ch === "+") && t.items.some((it) => it.ch === "-") &&
            core[i + 1] && isMetaTok(core[i + 1], "d", "+")
        ) {
            blocks.push({kind: "number"});
            i += 2;
        } else if (isMetaTok(t, "s", "+")) {
            blocks.push({kind: "space"});
            i++;
        } else if (isMetaTok(t, "q") && core[i + 1] && isMetaTok(core[i + 1], "Q", "+") && core[i + 2] && isMetaTok(core[i + 2], "q")) {
            blocks.push({kind: "quoted"});
            i += 3;
        } else {
            return null;
        }
    }
    return {blocks, anchorStart, anchorEnd};
}

/* ================================================================== */
/* Shared small pieces                                                 */
/* ================================================================== */


const standardInputClass = "focus:outline-none h-10 p-1 w-full mt-3 rounded-lg border border-solid";

/** Read-only v2 pattern field — populated only via the builder. */
function V2PatternInput({label, value, placeholder, onOpenBuilder, builderTooltip, onClear}) {
    const {errors} = useMemo(() => tokenizeV2(value || ""), [value]);
    const hasValue = (value || "").length > 0;
    const invalid = hasValue && errors.length > 0;
    return (
        <div className={`ml-1`}>
            <div className="flex flex-row items-center gap-x-2">
                <label>{label}</label>
                {hasValue && onClear && (
                    <Tooltip title={<div>Clear this pattern</div>} placement={"top"} enterDelay={500} arrow>
                        <IconButton size={"small"} onClick={onClear}>
                            <ClearIcon fontSize={"small"}/>
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            <input
                type="text"
                value={value}
                readOnly
                tabIndex={-1}
                spellCheck={false}
                autoComplete="off"
                onClick={onOpenBuilder}
                placeholder={placeholder}

                title={hasValue ? "Open the builder to change this pattern" : undefined}
                className={`${standardInputClass} cursor-pointer bg-gray-50 ${invalid ? "border-red-500" : hasValue ? "border-green-600 border-opacity-50" : "border-black border-opacity-25"}`}
            />
            {invalid && (
                <div className="mt-1">
                    {errors.slice(0, 3).map((e, idx) => (
                        <div key={idx} className="mb-1">
                            <pre className="text-red-700 overflow-x-auto m-0">
                                {(value || "") + "\n" + " ".repeat(e.pos) + "^"}
                            </pre>
                            <p className="text-red-700">{e.msg}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** ALLOWED/BLOCKED result rows with matched-span highlighting and blocked explanations. Matching is always case-insensitive, like the agent. */
function V2TestResults({tokens, ready, pathList, anchoredStart}) {
    const results = useMemo(() => {
        if (!ready) return [];
        return pathList.map((p) => {
            const m = matchV2(tokens, p, true);
            const diag = m === null ? diagnoseV2(tokens, p, true) : null;
            return {path: p, match: m, diag};
        });
    }, [ready, tokens, pathList]);

    if (!ready) {
        return pathList.length > 0
            ? <p className="text-gray-500">Finish the steps above to run the tests.</p>
            : null;
    }
    return (
        <div className="mt-2 flex flex-col gap-y-2">
            {results.map((r, idx) => (
                <div key={idx}
                     className={`rounded-lg border border-solid px-3 py-2 flex flex-row items-start gap-x-3 ${r.match ? "border-green-300 bg-green-50" : "border-gray-300 bg-white"}`}>
                    <span className={`mt-0.5 rounded-full px-2 py-0.5 font-bold ${r.match ? "bg-green-600 text-white" : "bg-gray-300 text-gray-700"}`}>
                        {r.match ? "ALLOWED" : "BLOCKED"}
                    </span>
                    <div className="min-w-0">
                        <pre className="m-0 whitespace-pre-wrap break-all">
                            {r.match ? (
                                <>
                                    {r.path.slice(0, r.match.start)}
                                    <mark className="bg-green-200">{r.path.slice(r.match.start, r.match.end)}</mark>
                                    {r.path.slice(r.match.end)}
                                </>
                            ) : r.diag ? (
                                <>
                                    {r.path.slice(0, r.diag.start)}
                                    {r.diag.pos > r.diag.start && (
                                        <mark className="bg-yellow-200">{r.path.slice(r.diag.start, r.diag.pos)}</mark>
                                    )}
                                    {r.diag.pos < r.path.length && (
                                        <mark className="bg-red-300 font-bold">{r.path[r.diag.pos]}</mark>
                                    )}
                                    {r.path.slice(Math.min(r.diag.pos + 1, r.path.length))}
                                </>
                            ) : r.path}
                        </pre>
                        {!r.match && r.diag && (
                            <p className="text-red-700 mt-0.5">{blockedMessage(r.diag, r.path, anchoredStart)}</p>
                        )}
                    </div>
                </div>
            ))}
            {pathList.length === 0 && (
                <p className="text-gray-400">Add a path above to see whether the agent would allow it.</p>
            )}
        </div>
    );
}

/* ================================================================== */
/* Stacked builder modal (opens on top of the create modal)            */
/* ================================================================== */

let builderSegmentId = 1;

export function V2PathBuilderModal({isOpen, onClose, onApply, fieldLabel, initialPattern, readOnly = false}) {
    const [start, setStart] = useState({kind: "drive", letter: "c", text: ""});
    const [segments, setSegments] = useState([]);
    const [file, setFile] = useState({kind: "anyExt", name: "", prefix: "", ext: "exe"});
    const [testPaths, setTestPaths] = useState("");
    const [loadNotice, setLoadNotice] = useState(null);

    // when opening: load the field's existing pattern into the builder, else start fresh
    useEffect(() => {
        if (isOpen) {
            let loaded = false;
            setLoadNotice(null);
            const existing = (initialPattern || "").trim();
            if (existing.length > 0) {
                const parsed = parsePathPatternToBuilder(existing);
                if (parsed) {
                    setStart(parsed.start);
                    setSegments(parsed.segments.map((s) => ({id: builderSegmentId++, text: "", ...s})));
                    setFile({kind: "anyExt", name: "", prefix: "", ext: "", ...parsed.file});
                    loaded = true;
                } else {
                    setLoadNotice(readOnly
                        ? "This pattern couldn't be broken into builder steps — it may be a v1 pattern or use features the builder doesn't cover. You can still test it below."
                        : "The existing pattern couldn't be loaded into the builder — using 'Use This Pattern' will replace it.");
                }
            }
            if (!loaded) {
                if (readOnly) {
                    setStart({kind: "none", letter: "c", text: ""});
                    setSegments([]);
                    setFile({kind: "anyFile", name: "", prefix: "", ext: ""});
                } else {
                    setStart({kind: "drive", letter: "c", text: ""});
                    setSegments([
                        {id: builderSegmentId++, kind: "exact", text: "program files"},
                        {id: builderSegmentId++, kind: "anyone", text: ""},
                    ]);
                    setFile({kind: "anyExt", name: "", prefix: "", ext: "exe"});
                }
            }
            setTestPaths("");
        }
    }, [isOpen, initialPattern, readOnly]);

    const problems = useMemo(() => builderProblems({start, segments, file}), [start, segments, file]);
    const builtPattern = useMemo(() => buildPattern({start, segments, file}), [start, segments, file]);
    const pattern = readOnly ? (initialPattern || "").trim() : builtPattern;
    const tokenized = useMemo(() => tokenizeV2(pattern), [pattern]);
    const tokens = tokenized.tokens;
    const ready = readOnly ? tokenized.errors.length === 0 : problems.length === 0;
    const showSteps = !readOnly || loadNotice === null;
    const examples = useMemo(
        () => (ready ? generateV2Examples(tokens, {argsMode: false}) : {working: null, failing: null}),
        [ready, tokens]
    );
    const pathList = useMemo(
        () => testPaths.split("\n").map((p) => p.replace(/\r$/, "")).filter((p) => p.trim().length > 0),
        [testPaths]
    );
    const updateSegment = (id, patch) => setSegments((segs) => segs.map((s) => (s.id === id ? {...s, ...patch} : s)));
    const removeSegment = (id) => setSegments((segs) => segs.filter((s) => s.id !== id));
    const insertSegmentAfter = (id) =>
        setSegments((segs) => {
            const i = segs.findIndex((s) => s.id === id);
            const copy = segs.slice();
            copy.splice(i + 1, 0, {id: builderSegmentId++, kind: "exact", text: ""});
            return copy;
        });
    const moveSegment = (id, dir) =>
        setSegments((segs) => {
            const i = segs.findIndex((s) => s.id === id);
            const j = i + dir;
            if (j < 0 || j >= segs.length) return segs;
            const copy = segs.slice();
            [copy[i], copy[j]] = [copy[j], copy[i]];
            return copy;
        });
    const startOver = () => {
        setStart({kind: "drive", letter: "c", text: ""});
        setSegments([
            {id: builderSegmentId++, kind: "exact", text: "program files"},
            {id: builderSegmentId++, kind: "anyone", text: ""},
        ]);
        setFile({kind: "anyExt", name: "", prefix: "", ext: "exe"});
        setTestPaths("");
    };
    const fileDef = FILE_KINDS.find((k) => k.kind === file.kind);
    const smallInputClass = "focus:outline-none h-9 p-1 rounded-lg border border-black border-opacity-25 border-solid";

    return (
        <Modal contentLabel={`${readOnly ? "View" : "Build"} ${fieldLabel}`}
               isOpen={isOpen}
               onRequestClose={onClose}
               shouldCloseOnOverlayClick={true}
               className={`focus:outline-none focus:shadow-sm border-2 flex relative z-50 bg-white w-4xl max-w-4xl inset-y-10 mx-auto rounded-2xl`}
               overlayClassName="z-50 bg-black bg-opacity-5 fixed inset-0 overflow-scroll"
        >
            <div className="flex flex-1 flex-col p-8 w-full ml-4 mr-4">
                <div className="flex flex-1 flex-col gap-y-5">
                    <div className="flex flex-row justify-between">
                        <h1 className="font-bold text-3xl">{readOnly ? "View" : "Build"} {fieldLabel}</h1>
                        <MuiCloseIconButton onClick={onClose}/>
                    </div>
                    <hr className="h-0.5"/>
                    {loadNotice && (
                        <div className="ml-1 rounded-lg border border-yellow-400 border-solid bg-yellow-50 px-3 py-2">
                            <p className="text-yellow-800 m-0">{loadNotice}</p>
                        </div>
                    )}
                    <label className="ml-1 text-gray-600">
                        {readOnly
                            ? "Read-only view of this pattern broken down into builder steps. Use the tester at the bottom to try it against real paths."
                            : "Describe the allowed program location step by step, special characters in your text are escaped automatically."}
                    </label>

                    {showSteps && (
                        <>
                            {/* Step 1: start */}
                            <div className="ml-1">
                                <label className="font-bold">1. The path starts with</label>
                                <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-2 mt-2">
                                    <select className={`${smallInputClass} bg-gray-50 ${readOnly ? "" : "cursor-pointer"}`} value={start.kind} disabled={readOnly}
                                            onChange={(e) => setStart({...start, kind: e.target.value})}>
                                        {START_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                                    </select>
                                    {start.kind === "drive" && (
                                        <label className="flex flex-row items-center gap-x-1">
                                            Drive letter
                                            <input className={`${smallInputClass} w-12 text-center`} maxLength={1}
                                                   value={start.letter} disabled={readOnly}
                                                   onChange={(e) => setStart({...start, letter: e.target.value})}/>
                                            <span>:</span>
                                        </label>
                                    )}
                                    {start.kind === "custom" && (
                                        <input className={`${smallInputClass} flex-1`}
                                               placeholder={"\\\\fileserver\\share"} value={start.text} disabled={readOnly}
                                               onChange={(e) => setStart({...start, text: e.target.value})}/>
                                    )}
                                </div>
                            </div>

                            {/* Step 2: folders */}
                            <div className="ml-1">
                                <label className="font-bold">2. Then goes through these folders, in order</label>
                                <div className="flex flex-col gap-y-2 mt-2">
                                    {segments.map((seg, i) => {
                                        const def = SEGMENT_KINDS.find((k) => k.kind === seg.kind);
                                        return (
                                            <div key={seg.id}
                                                 className="flex flex-row items-center gap-x-2 rounded-lg border border-gray-300 border-solid bg-white px-2 py-2">
                                                <span className="text-gray-400 w-5 text-right">{i + 1}.</span>
                                                <select className={`${smallInputClass} bg-gray-50 ${readOnly ? "" : "cursor-pointer"}`} value={seg.kind} disabled={readOnly}
                                                        onChange={(e) => updateSegment(seg.id, {kind: e.target.value})}>
                                                    {SEGMENT_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                                                </select>
                                                {def && def.needsText ? (
                                                    <input className={`${smallInputClass} flex-1 min-w-0`}
                                                           placeholder={def.placeholder} value={seg.text} disabled={readOnly}
                                                           onChange={(e) => updateSegment(seg.id, {text: e.target.value})}/>
                                                ) : (
                                                    <span className="flex-1 text-gray-400 italic px-1">
                                                        {seg.kind === "anydeep" ? "matches one or more nested folder levels" : "matches exactly one folder, any name"}
                                                    </span>
                                                )}
                                                {!readOnly && (
                                                    <>
                                                        <button type="button" onClick={() => insertSegmentAfter(seg.id)}
                                                                title="Add a folder step below this one"
                                                                className="px-1.5 py-1 text-gray-400 hover:text-gray-700">＋
                                                        </button>
                                                        <button type="button" onClick={() => moveSegment(seg.id, -1)} disabled={i === 0}
                                                                title="Move up"
                                                                className="px-1.5 py-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">↑
                                                        </button>
                                                        <button type="button" onClick={() => moveSegment(seg.id, 1)}
                                                                disabled={i === segments.length - 1} title="Move down"
                                                                className="px-1.5 py-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">↓
                                                        </button>
                                                        <button type="button" onClick={() => removeSegment(seg.id)} title="Remove this step"
                                                                className="px-1.5 py-1 text-gray-400 hover:text-red-700">✕
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {segments.length === 0 && (
                                        <p className="text-gray-400 italic m-0">No folder steps — the file sits directly at the start location.</p>
                                    )}
                                </div>
                                {!readOnly && (
                                    <button type="button"
                                            onClick={() => setSegments((s) => [...s, {id: builderSegmentId++, kind: "exact", text: ""}])}
                                            className="mt-2 rounded-lg border border-dashed border-gray-400 px-3 py-1.5 text-gray-700 hover:bg-gray-50">
                                        + Add folder step
                                    </button>
                                )}
                            </div>

                            {/* Step 3: file */}
                            <div className="ml-1">
                                <label className="font-bold">3. And ends at this file</label>
                                <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-2 mt-2 rounded-lg border border-gray-300 border-solid bg-white px-2 py-2">
                                    <select className={`${smallInputClass} bg-gray-50 ${readOnly ? "" : "cursor-pointer"}`} value={file.kind} disabled={readOnly}
                                            onChange={(e) => setFile({...file, kind: e.target.value})}>
                                        {FILE_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                                    </select>
                                    {fileDef && fileDef.fields.includes("name") && (
                                        <input className={`${smallInputClass} flex-1 min-w-0`} placeholder="agent.exe"
                                               value={file.name} disabled={readOnly}
                                               onChange={(e) => setFile({...file, name: e.target.value})}/>
                                    )}
                                    {fileDef && fileDef.fields.includes("prefix") && (
                                        <input className={`${smallInputClass} flex-1 min-w-0`} placeholder="updater"
                                               value={file.prefix} disabled={readOnly}
                                               onChange={(e) => setFile({...file, prefix: e.target.value})}/>
                                    )}
                                    {fileDef && fileDef.fields.includes("ext") && (
                                        <label className="flex flex-row items-center gap-x-1">
                                            <span>.</span>
                                            <input className={`${smallInputClass} w-20`} placeholder="exe"
                                                   value={file.ext} disabled={readOnly}
                                                   onChange={(e) => setFile({...file, ext: e.target.value})}/>
                                        </label>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Problems */}
                    {!readOnly && problems.length > 0 && (
                        <div className="ml-1 rounded-lg border border-yellow-400 border-solid bg-yellow-50 px-3 py-2">
                            <p className="font-bold text-yellow-800 m-0 mb-1">Finish these before using the pattern:</p>
                            {problems.map((p, i) => (
                                <p key={i} className="text-yellow-800 m-0">• {p}</p>
                            ))}
                        </div>
                    )}

                    {/* Pattern */}
                    <div className="ml-1">
                        <label className="font-bold">{readOnly ? "v2 pattern" : "Generated v2 pattern"}</label>
                        <pre className="mt-2 whitespace-pre-wrap break-all rounded-lg border border-gray-300 border-solid bg-gray-50 px-3 py-2 m-0 select-all"
                        >
                            {pattern.length > 0 ? pattern : "\u00A0"}
                        </pre>
                    </div>

                    {/* Auto-generated example */}
                    {ready && examples.working !== null && (
                        <div className="ml-1">
                            <label className="font-bold">Example against {readOnly ? "this pattern" : "what you've built"}</label>
                            <V2TestResults tokens={tokens} ready={ready}
                                           pathList={[examples.working]}
                                           anchoredStart={true}/>
                        </div>
                    )}

                    {/* Tester */}
                    <div className="ml-1">
                        <label className="font-bold">Try it against real paths (one per line)</label>
                        <textarea value={testPaths} onChange={(e) => setTestPaths(e.target.value)} spellCheck={false}
                                  rows={3}
                                  placeholder={"c:\\program files\\vendor\\agent.exe"}
                                  className="focus:outline-none p-2 w-full mt-2 rounded-lg border border-black border-opacity-25 border-solid"/>
                        <V2TestResults tokens={tokens} ready={ready} pathList={pathList}
                                       anchoredStart={true}/>
                    </div>

                    {/* Actions */}
                    {readOnly ? (
                        <div className="flex flex-row justify-end gap-x-3">
                            <ThemeProvider theme={buttonTheme}>
                                <Button variant={"contained"} color={"primary"} onClick={onClose}>Close</Button>
                            </ThemeProvider>
                        </div>
                    ) : (
                        <div className="flex flex-row justify-between gap-x-3">
                            <ThemeProvider theme={buttonTheme}>
                                <Button variant={"outlined"} color={"primary"} onClick={startOver}>Start Over</Button>
                            </ThemeProvider>
                            <div className="flex flex-row gap-x-3">
                                <ThemeProvider theme={buttonTheme}>
                                    <Button variant={"outlined"} color={"primary"} onClick={onClose}>Cancel</Button>
                                    <Button variant={"contained"} color={"primary"} disabled={!ready}
                                            onClick={() => {
                                                onApply(pattern);
                                                onClose();
                                            }}>
                                        Use This Pattern
                                    </Button>
                                </ThemeProvider>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
/* ================================================================== */
/* Stacked arguments builder modal                                     */
/* Blocks concatenate left to right; the pattern is anchored so it     */
/* must describe the entire argument string.                           */
/* ================================================================== */

export function V2ArgsBuilderModal({isOpen, onClose, onApply, fieldLabel, initialPattern, readOnly = false}) {
    const [blocks, setBlocks] = useState([]);
    const [anchorStart, setAnchorStart] = useState(false);
    const [anchorEnd, setAnchorEnd] = useState(false);
    const [rawSource, setRawSource] = useState("");
    const [testStrings, setTestStrings] = useState("");
    const [loadNotice, setLoadNotice] = useState(null);

    useEffect(() => {
        if (isOpen) {
            let loaded = false;
            setLoadNotice(null);
            setRawSource("");
            const existing = (initialPattern || "").trim();
            if (existing.length > 0) {
                const parsed = parseArgsPatternToBuilder(existing);
                if (parsed) {
                    setBlocks(parsed.blocks.map((b) => ({id: builderSegmentId++, text: "", ...b})));
                    setAnchorStart(parsed.anchorStart);
                    setAnchorEnd(parsed.anchorEnd);
                    //an autofilled exact-match pattern loads as one big literal block; offer its
                    //text in the splitter so the user can break it apart and generalize pieces
                    if (parsed.blocks.length === 1 && parsed.blocks[0].kind === "literal") {
                        setRawSource(parsed.blocks[0].text);
                    }
                    loaded = true;
                } else {
                    setLoadNotice(readOnly
                        ? "This pattern couldn't be broken into builder blocks — it may be a v1 pattern or use features the builder doesn't cover. You can still test it below."
                        : "The existing pattern couldn't be loaded into the builder — using 'Use This Pattern' will replace it.");
                }
            }
            if (!loaded) {
                if (readOnly) {
                    //don't show a misleading starter block in the read-only view
                    setBlocks([]);
                    setAnchorStart(existing.startsWith("^"));
                    setAnchorEnd(existing.endsWith("$"));
                } else {
                    //the agent implicitly wildcards the start and end of argument patterns, so a lone
                    //text block already means "arguments contain…" — anchors are opt-in below
                    setBlocks([
                        {id: builderSegmentId++, kind: "literal", text: ""},
                    ]);
                    setAnchorStart(false);
                    setAnchorEnd(false);
                }
            }
            setTestStrings("");
        }
    }, [isOpen, initialPattern, readOnly]);

    const problems = useMemo(() => argsProblems(blocks), [blocks]);
    const builtPattern = useMemo(() => buildArgsPattern(blocks, anchorStart, anchorEnd), [blocks, anchorStart, anchorEnd]);
    //in read-only mode the stored pattern is the source of truth (even if it couldn't be
    //broken into blocks), so the pattern box, example, and tester always reflect it exactly
    const pattern = readOnly ? (initialPattern || "").trim() : builtPattern;
    const tokenized = useMemo(() => tokenizeV2(pattern), [pattern]);
    const tokens = tokenized.tokens;
    const ready = readOnly ? tokenized.errors.length === 0 : problems.length === 0;
    const showBlocks = !readOnly || loadNotice === null;
    const anchorStartEff = readOnly ? pattern.startsWith("^") : anchorStart;
    const anchorEndEff = readOnly ? pattern.endsWith("$") : anchorEnd;
    const examples = useMemo(
        () => (ready ? generateV2Examples(tokens, {argsMode: true, anchorStart: anchorStartEff, anchorEnd: anchorEndEff}) : {working: null, failing: null}),
        [ready, tokens, anchorStartEff, anchorEndEff]
    );

    const stringList = useMemo(
        () => testStrings.split("\n").map((p) => p.replace(/\r$/, "")).filter((p) => p.trim().length > 0),
        [testStrings]
    );

    const updateBlock = (id, patch) => setBlocks((bs) => bs.map((b) => (b.id === id ? {...b, ...patch} : b)));
    const removeBlock = (id) => setBlocks((bs) => bs.filter((b) => b.id !== id));
    const insertBlockAfter = (id) =>
        setBlocks((bs) => {
            const i = bs.findIndex((b) => b.id === id);
            const copy = bs.slice();
            copy.splice(i + 1, 0, {id: builderSegmentId++, kind: "literal", text: ""});
            return copy;
        });
    const moveBlock = (id, dir) =>
        setBlocks((bs) => {
            const i = bs.findIndex((b) => b.id === id);
            const j = i + dir;
            if (j < 0 || j >= bs.length) return bs;
            const copy = bs.slice();
            [copy[i], copy[j]] = [copy[j], copy[i]];
            return copy;
        });

    const startOver = () => {
        setBlocks([
            {id: builderSegmentId++, kind: "literal", text: ""},
        ]);
        setAnchorStart(false);
        setAnchorEnd(false);
        setRawSource("");
        setTestStrings("");
    };

    const smallInputClass = "focus:outline-none h-9 p-1 rounded-lg border border-black border-opacity-25 border-solid";

    return (
        <Modal contentLabel={`Build ${fieldLabel}`}
               isOpen={isOpen}
               onRequestClose={onClose}
               shouldCloseOnOverlayClick={true}
               className={`focus:outline-none focus:shadow-sm border-2 flex relative z-50 bg-white w-4xl max-w-4xl inset-y-10 mx-auto rounded-2xl`}
               overlayClassName="z-50 bg-black bg-opacity-5 fixed inset-0 overflow-scroll"
        >
            <div className="flex flex-1 flex-col p-8 w-full ml-4 mr-4">
                <div className="flex flex-1 flex-col gap-y-5">
                    <div className="flex flex-row justify-between">
                        <h1 className="font-bold text-3xl">{readOnly ? "View" : "Build"} {fieldLabel}</h1>
                        <MuiCloseIconButton onClick={onClose}/>
                    </div>
                    <hr className="h-0.5"/>
                    {loadNotice && (
                        <div className="ml-1 rounded-lg border border-yellow-400 border-solid bg-yellow-50 px-3 py-2">
                            <p className="text-yellow-800 m-0">{loadNotice}</p>
                        </div>
                    )}
                    <label className="ml-1 text-gray-600">
                        {readOnly
                            ? "Read-only view of this pattern broken down into builder blocks. Use the tester at the bottom to try it against real argument strings."
                            : 'The agent by default treats the argument patterns as a "contains", so anything may appear before and after what you describe below. If the arguments must start or end exactly where your blocks do, opt in with the toggles under the blocks. Special characters in your text are escaped automatically.'}
                    </label>

                    {/* Start from a real argument string */}
                    {!readOnly && (
                    <div className="ml-1">
                        <label className="font-bold">Start from a real argument string (optional)</label>
                        <div className="flex flex-row items-center gap-x-2 mt-2">
                            <input className={`${smallInputClass} flex-1 min-w-0`}
                                   placeholder={'-F --color=auto "C:\\some path\\file.exe"'}
                                   value={rawSource}
                                   spellCheck={false}
                                   autoComplete="off"
                                   onKeyPress={(e) => {
                                       if (e.key === 'Enter') e.preventDefault();
                                   }}
                                   onChange={(e) => setRawSource(e.target.value)}/>
                            <ThemeProvider theme={buttonTheme}>
                                <Button variant={"outlined"} color={"primary"}
                                        onClick={() => {
                                            if (!rawSource.trim()) {
                                                return; //nothing to split yet
                                            }
                                            setBlocks(splitRawArgsToBlocks(rawSource).map((b) => ({id: builderSegmentId++, ...b})));
                                        }}>
                                    Split Into Blocks
                                </Button>
                            </ThemeProvider>
                        </div>
                        <label className="text-gray-600">
                            Splits on spaces and quoted sections into exact-text blocks, replacing the blocks below.
                            Then generalize the pieces that vary — e.g. change a piece to 'A number' or 'A quoted value'.
                        </label>
                    </div>
                    )}

                    {/* Blocks */}
                    {showBlocks && (
                    <div className="ml-1">
                        <label className="font-bold">The argument string is, in order</label>
                        <div className="flex flex-col gap-y-2 mt-2">
                            {blocks.map((b, i) => {
                                const def = ARG_KINDS.find((k) => k.kind === b.kind);
                                return (
                                    <div key={b.id}
                                         className="flex flex-row items-center gap-x-2 rounded-lg border border-gray-300 border-solid bg-white px-2 py-2">
                                        <span className="text-gray-400 w-5 text-right">{i + 1}.</span>
                                        <select className={`${smallInputClass} bg-gray-50 ${readOnly ? "" : "cursor-pointer"}`} value={b.kind} disabled={readOnly}
                                                onChange={(e) => updateBlock(b.id, {kind: e.target.value})}>
                                            {ARG_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                                        </select>
                                        {def && def.needsText ? (
                                            <input className={`${smallInputClass} flex-1 min-w-0`}
                                                   placeholder={def.placeholder} value={b.text} disabled={readOnly}
                                                   onChange={(e) => updateBlock(b.id, {text: e.target.value})}/>
                                        ) : (
                                            <span className="flex-1"/>
                                        )}
                                        {!readOnly && (
                                            <>
                                                <button type="button" onClick={() => insertBlockAfter(b.id)}
                                                        title="Add a block below this one"
                                                        className="px-1.5 py-1 text-gray-400 hover:text-gray-700">＋
                                                </button>
                                                <button type="button" onClick={() => moveBlock(b.id, -1)} disabled={i === 0}
                                                        title="Move up"
                                                        className="px-1.5 py-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">↑
                                                </button>
                                                <button type="button" onClick={() => moveBlock(b.id, 1)}
                                                        disabled={i === blocks.length - 1} title="Move down"
                                                        className="px-1.5 py-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">↓
                                                </button>
                                                <button type="button" onClick={() => removeBlock(b.id)} title="Remove this block"
                                                        className="px-1.5 py-1 text-gray-400 hover:text-red-700">✕
                                                </button>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                            {blocks.length === 0 && (
                                <p className="text-gray-400 italic m-0">No blocks yet — add at least one.</p>
                            )}
                        </div>
                        {!readOnly && (
                            <button type="button"
                                    onClick={() => setBlocks((bs) => [...bs, {id: builderSegmentId++, kind: "literal", text: ""}])}
                                    className="mt-2 rounded-lg border border-dashed border-gray-400 px-3 py-1.5 text-gray-700 hover:bg-gray-50">
                                + Add block
                            </button>
                        )}
                        <div className="flex flex-col mt-2 ml-3">
                            <FormControlLabel
                                control={<input type="checkbox" className="mr-1" checked={anchorStartEff} disabled={readOnly}
                                                onChange={(e) => setAnchorStart(e.target.checked)}/>}
                                label={<span>Arguments must start with the first block (adds ^ — otherwise anything may come before it)</span>}
                            />
                            <FormControlLabel
                                control={<input type="checkbox" className="mr-1" checked={anchorEndEff} disabled={readOnly}
                                                onChange={(e) => setAnchorEnd(e.target.checked)}/>}
                                label={<span>Arguments must end with the last block (adds $ — otherwise anything may come after it)</span>}
                            />
                        </div>
                    </div>
                    )}

                    {/* Problems */}
                    {!readOnly && problems.length > 0 && (
                        <div className="ml-1 rounded-lg border border-yellow-400 border-solid bg-yellow-50 px-3 py-2">
                            <p className="font-bold text-yellow-800 m-0 mb-1">Finish these before using the pattern:</p>
                            {problems.map((p, i) => (
                                <p key={i} className="text-yellow-800 m-0">• {p}</p>
                            ))}
                        </div>
                    )}

                    {/* Generated pattern */}
                    <div className="ml-1">
                        <label className="font-bold">{readOnly ? "v2 pattern" : "Generated v2 pattern"}</label>
                        <pre className="mt-2 whitespace-pre-wrap break-all rounded-lg border border-gray-300 border-solid bg-gray-50 px-3 py-2 m-0 select-all"
                        >
                            {pattern.length > 0 ? pattern : "\u00A0"}
                        </pre>
                    </div>

                    {/* Auto-generated examples */}
                    {ready && examples.working !== null && (
                        <div className="ml-1">
                            <label className="font-bold">Example against {readOnly ? "this pattern" : "what you've built"}</label>
                            <V2TestResults tokens={tokens} ready={ready}
                                           pathList={[examples.working]}
                                           anchoredStart={anchorStartEff}/>
                        </div>
                    )}

                    {/* Tester */}
                    <div className="ml-1">
                        <label className="font-bold">Try it against real argument strings (one per line)</label>
                        <textarea value={testStrings} onChange={(e) => setTestStrings(e.target.value)} spellCheck={false}
                                  rows={3}
                                  placeholder={'-install "C:\\temp\\pkg.msi" /quiet'}
                                  className="focus:outline-none p-2 w-full mt-2 rounded-lg border border-black border-opacity-25 border-solid"/>
                        <V2TestResults tokens={tokens} ready={ready} pathList={stringList}
                                       anchoredStart={anchorStartEff}/>
                    </div>

                    {/* Actions */}
                    {readOnly ? (
                        <div className="flex flex-row justify-end gap-x-3">
                            <ThemeProvider theme={buttonTheme}>
                                <Button variant={"contained"} color={"primary"} onClick={onClose}>Close</Button>
                            </ThemeProvider>
                        </div>
                    ) : (
                        <div className="flex flex-row justify-between gap-x-3">
                            <ThemeProvider theme={buttonTheme}>
                                <Button variant={"outlined"} color={"primary"} onClick={startOver}>Start Over</Button>
                            </ThemeProvider>
                            <div className="flex flex-row gap-x-3">
                                <ThemeProvider theme={buttonTheme}>
                                    <Button variant={"outlined"} color={"primary"} onClick={onClose}>Cancel</Button>
                                    <Button variant={"contained"} color={"primary"} disabled={!ready}
                                            onClick={() => {
                                                onApply(pattern);
                                                onClose();
                                            }}>
                                        Use This Pattern
                                    </Button>
                                </ThemeProvider>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

/* ================================================================== */
/* Create v2 Tailored Behavior modal (Option B)                        */
/* Mirrors the v1 create modal; the two path fields launch the         */
/* stacked builder modal, all four v2 fields validate live.            */
/* ================================================================== */

export function CreateV2TailoredBehaviorModal({
                                                  isOpen,
                                                  onClose,
                                                  zenGroupDropdownOptionsList,
                                                  fileTriggersToMatchAllOptions,
                                                  handleFileTriggerTypesSelected,
                                                  onSubmit,
                                                  defaultZenGroupId,
                                                  defaultPath,
                                                  defaultProgramArguments,
                                                  defaultParentPath,
                                                  defaultParentProgramArguments,
                                              }) {
    const [zenGroup, setZenGroup] = useState();
    const [whitelistName, setWhitelistName] = useState("");
    const [path, setPath] = useState("");
    const [programArguments, setProgramArguments] = useState("");
    const [parentProgramToggled, setParentProgramToggled] = useState(false);
    const [parentPath, setParentPath] = useState("");
    const [parentProgramArguments, setParentProgramArguments] = useState("");
    const [additionalRestrictionsToggled, setAdditionalRestrictionsToggled] = useState(false);
    const [onlyMatchIfTrustedCert, setOnlyMatchIfTrustedCert] = useState(false);
    const [onlyMatchIfNoInjections, setOnlyMatchIfNoInjections] = useState(false);
    const [onlyMatchIfNoModifiedMemory, setOnlyMatchIfNoModifiedMemory] = useState(false);
    const [incidentMatchTypeList, setIncidentMatchTypeList] = useState(fileTriggersToMatchAllOptions);
    const [temporaryToggled, setTemporaryToggled] = useState(false);
    const [expirationHours, setExpirationHours] = useState("");
    const [hideCanariesToggled, setHideCanariesToggled] = useState(false);
    const [builderTarget, setBuilderTarget] = useState(null); // null | 'path' | 'parentPath' | 'programArguments' | 'parentProgramArguments'
    const [submitting, setSubmitting] = useState(false);

    /*
        Autofill from another page (e.g. an incident): raw values arrive via props and are
        translated into exact-match v2 patterns. Like the v1 modal, parent values are seeded
        but the parent toggle stays off — they appear if the user toggles it on. The seeded
        patterns are builder-shaped, so the user can open the builder to edit them.
    */
    useEffect(() => {
        if (isOpen) {
            if (defaultZenGroupId) setZenGroup(defaultZenGroupId);
            if (defaultPath) setPath(rawToExactV2Pattern(defaultPath));
            if (defaultProgramArguments) setProgramArguments(rawToExactV2Pattern(defaultProgramArguments));
            if (defaultParentPath) setParentPath(rawToExactV2Pattern(defaultParentPath));
            if (defaultParentProgramArguments) setParentProgramArguments(rawToExactV2Pattern(defaultParentProgramArguments));
        }
    }, [isOpen, defaultZenGroupId, defaultPath, defaultProgramArguments, defaultParentPath, defaultParentProgramArguments]);

    function resetAndClose() {
        setZenGroup();
        setWhitelistName("");
        setPath("");
        setProgramArguments("");
        setParentProgramToggled(false);
        setParentPath("");
        setParentProgramArguments("");
        setAdditionalRestrictionsToggled(false);
        setOnlyMatchIfTrustedCert(false);
        setOnlyMatchIfNoInjections(false);
        setOnlyMatchIfNoModifiedMemory(false);
        setIncidentMatchTypeList(fileTriggersToMatchAllOptions);
        setTemporaryToggled(false);
        setExpirationHours("");
        setHideCanariesToggled(false);
        setBuilderTarget(null);
        setSubmitting(false);
        onClose();
    }

    function validateV2Field(value, fieldLabel, required) {
        const trimmed = (value || "").trim();
        if (!trimmed) {
            if (required) {
                NotificationManager.error(`Please fill out the ${fieldLabel}`);
                return undefined;
            }
            return null; // optional and empty
        }
        const {errors} = tokenizeV2(trimmed);
        if (errors.length > 0) {
            NotificationManager.error(`${fieldLabel} is not a valid v2 pattern: ${errors[0].msg}`);
            return undefined;
        }
        return trimmed;
    }

    function submit() {
        if (!zenGroup) {
            NotificationManager.error("Please make sure all fields are filled out and try again.");
            return;
        }
        const pathToSend = validateV2Field(path, "Program Path", true);
        if (pathToSend === undefined) return;
        const programArgumentsToSend = validateV2Field(programArguments, "Program Arguments", false);
        if (programArgumentsToSend === undefined) return;
        let parentPathToSend = null;
        let parentProgramArgsToSend = null;
        if (parentProgramToggled) {
            parentPathToSend = validateV2Field(parentPath, "Parent Program Path", true);
            if (parentPathToSend === undefined) return;
            parentProgramArgsToSend = validateV2Field(parentProgramArguments, "Parent Program Arguments", false);
            if (parentProgramArgsToSend === undefined) return;
        }
        let tempHoursToSend = 0;
        if (temporaryToggled) {
            const hours = parseInt(expirationHours, 10);
            if (!hours || hours < 1) {
                NotificationManager.error("Please Enter the Hours Until This Tailored Behavior Expires");
                return;
            }
            tempHoursToSend = hours;
        }
        let onlyMatchIfTrustedCertToSend = false;
        let onlyMatchIfNoInjectionsToSend = false;
        let onlyMatchIfNoModifiedMemoryToSend = false;
        let fileTriggersSelected = null;
        if (additionalRestrictionsToggled) {
            onlyMatchIfTrustedCertToSend = onlyMatchIfTrustedCert;
            onlyMatchIfNoInjectionsToSend = onlyMatchIfNoInjections;
            onlyMatchIfNoModifiedMemoryToSend = onlyMatchIfNoModifiedMemory;
            if (incidentMatchTypeList && incidentMatchTypeList.length > 0) {
                fileTriggersSelected = handleFileTriggerTypesSelected(incidentMatchTypeList);
            }
        }
        const nameToSend = (whitelistName || "").trim().length > 0 ? whitelistName.trim() : null;

        setSubmitting(true);
        onSubmit({
            path: pathToSend,
            zenGroupId: zenGroup,
            programArguments: programArgumentsToSend,
            whitelistName: nameToSend,
            parentPath: parentPathToSend,
            parentProgramArguments: parentProgramArgsToSend,
            onlyMatchIfTrustedCert: onlyMatchIfTrustedCertToSend,
            onlyMatchIfNoInjections: onlyMatchIfNoInjectionsToSend,
            onlyMatchIfNoModifiedMemory: onlyMatchIfNoModifiedMemoryToSend,
            fileTriggersSelected: fileTriggersSelected,
            expirationHours: tempHoursToSend,
            hideCanaries: hideCanariesToggled,
        }).then(() => {
            resetAndClose();
        }).catch(() => {
            setSubmitting(false);
        });
    }

    return (
        <Modal contentLabel="Create v2 Tailored Behavior"
               isOpen={isOpen}
               onRequestClose={resetAndClose}
               shouldCloseOnOverlayClick={true}
               className={`focus:outline-none focus:shadow-sm border-2 flex relative z-50 bg-white w-3xl max-w-3xl inset-y-10 mx-auto rounded-2xl`}
               overlayClassName="z-50 bg-black bg-opacity-5 fixed inset-0 overflow-scroll"
        >
            <div className="flex flex-1 flex-col p-8 w-full ml-4 mr-4">
                <div className="flex flex-1 flex-col gap-y-5">
                    {/*Title with exit button*/}
                    <div className="flex flex-row justify-between">
                        <h1 className="font-bold text-3xl">Add Path to Tailored Behaviors</h1>
                        <MuiCloseIconButton onClick={resetAndClose}/>
                    </div>
                    <hr className="h-0.5"/>
                    {/*Form content*/}
                    <div className="ml-1">
                        <label>Select which group to add to</label>
                        <MuiAutocompleteForZenGroupsWithoutCreateGroupOption
                            zenGroupDropdownOptionsList={zenGroupDropdownOptionsList}
                            value={zenGroup}
                            onChange={(event, value) => {
                                setZenGroup(value ? value.value : undefined)
                            }}
                        />
                    </div>
                    <div className="ml-1">
                        <label>Name for the tailored behavior</label>
                        <br/>
                        <input
                            type="text"
                            value={whitelistName}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') e.preventDefault();
                            }}
                            onChange={(e) => setWhitelistName(e.target.value)}
                            placeholder={"Optional"}
                            className={`${standardInputClass} border-black border-opacity-25`}
                        />
                    </div>
                    <div className="ml-1">
                        <label>
                            We have updated our pattern matching to be more customizable for users, which we refer to as v2. Now, the v2 program path and program arguments are created with a guided builder.
                        </label>
                    </div>

                    <V2PatternInput
                        label={"Program Path"}
                        value={path}
                        placeholder={"Required — click to build"}
                        onOpenBuilder={() => setBuilderTarget('path')}
                        builderTooltip={"Build the Program Path pattern step by step"}
                        onClear={() => setPath("")}
                    />

                    <V2PatternInput
                        label={"Program Arguments (Optional)"}
                        value={programArguments}
                        placeholder={"Optional — click to build"}
                        onOpenBuilder={() => setBuilderTarget('programArguments')}
                        builderTooltip={"Build the Program Arguments pattern step by step"}
                        onClear={() => setProgramArguments("")}
                    />

                    <div className={`flex flex-row items-center ml-1`}>
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={parentProgramToggled}
                                    onChange={e => setParentProgramToggled(e.target.checked)}
                                />
                            }
                                              label={parentProgramToggled ? "Limit Exception to Program + Parent Program" : "Do Not Limit Exception to Program + Parent Program"}/>
                        </ThemeProvider>
                    </div>
                    {parentProgramToggled && (
                        <V2PatternInput
                            label={"Parent Program Path"}
                            value={parentPath}
                            placeholder={"Required — click to build"}
                            onOpenBuilder={() => setBuilderTarget('parentPath')}
                            builderTooltip={"Build the Parent Program Path pattern step by step"}
                            onClear={() => setParentPath("")}
                        />
                    )}
                    {parentProgramToggled && (
                        <V2PatternInput
                            label={"Parent Program Arguments (Optional)"}
                            value={parentProgramArguments}
                            placeholder={"Optional — click to build"}
                            onOpenBuilder={() => setBuilderTarget('parentProgramArguments')}
                            builderTooltip={"Build the Parent Program Arguments pattern step by step"}
                            onClear={() => setParentProgramArguments("")}
                        />
                    )}

                    <div className={`flex flex-row items-center ml-1`}>
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={additionalRestrictionsToggled}
                                    onChange={e => setAdditionalRestrictionsToggled(e.target.checked)}
                                />
                            }
                                              label={additionalRestrictionsToggled ? "Enter Additional Tailored Behavior Restrictions" : "Do Not Enter Additional Tailored Behavior Restrictions"}/>
                        </ThemeProvider>
                    </div>
                    <div className={`${additionalRestrictionsToggled ? "block ml-3" : "hidden"} flex flex-row items-center`}>
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={onlyMatchIfTrustedCert}
                                    onChange={e => setOnlyMatchIfTrustedCert(e.target.checked)}
                                />
                            }
                                              label={onlyMatchIfTrustedCert ? "Only Match Tailored Behavior if Trusted Cert" : "Do Not Limit Tailored Behavior to Match Trusted Certs Only"}/>
                        </ThemeProvider>
                        <MuiIconWithTooltip
                            icon={
                                <FontAwesomeIcon
                                    className="object-contain mt-1 cursor-pointer"
                                    icon="fa-light fa-circle-info"
                                    size="lg"
                                />
                            }
                            tooltipTitle={`This setting controls whether the tailored behavior should be applied to an extortion response regardless of if the response has a trusted cert or not, or only apply the 
                            tailored behavior when the extortion response has a trusted cert (meaning the response will be suspended otherwise). The default setting is to not limit.`}
                            tooltipPlacement={"bottom-start"}
                        />
                    </div>
                    <div className={`${additionalRestrictionsToggled ? "block ml-3" : "hidden"} flex flex-row items-center`}>
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={onlyMatchIfNoInjections}
                                    onChange={e => setOnlyMatchIfNoInjections(e.target.checked)}
                                />
                            }
                                              label={onlyMatchIfNoInjections ? "Only Match Tailored Behavior if No Related Injections" : "Do Not Limit Tailored Behavior to Match No Related Injections Only"}/>
                        </ThemeProvider>
                        <MuiIconWithTooltip
                            icon={
                                <FontAwesomeIcon
                                    className="object-contain mt-1 cursor-pointer"
                                    icon="fa-light fa-circle-info"
                                    size="lg"
                                />
                            }
                            tooltipTitle={`This setting controls if the tailored behavior should be applied to an extortion response regardless of if the response has related process injections or not, or only apply the
                            tailored behavior when the extortion response has no related process injections (meaning the response will be suspended otherwise). The default setting is to not limit.`}
                            tooltipPlacement={"bottom-start"}
                        />
                    </div>
                    <div className={`${additionalRestrictionsToggled ? "block ml-3" : "hidden"} flex flex-row items-center`}>
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={onlyMatchIfNoModifiedMemory}
                                    onChange={e => setOnlyMatchIfNoModifiedMemory(e.target.checked)}
                                />
                            }
                                              label={onlyMatchIfNoModifiedMemory ? "Only Match Tailored Behavior if No Modified Memory" : "Do Not Limit Tailored Behavior to Match No Modified Memory Only"}/>
                        </ThemeProvider>
                        <MuiIconWithTooltip
                            icon={
                                <FontAwesomeIcon
                                    className="object-contain mt-1 cursor-pointer"
                                    icon="fa-light fa-circle-info"
                                    size="lg"
                                />
                            }
                            tooltipTitle={`This setting controls if the tailored behavior should be applied to an extortion response regardless of if the response has modified memory or not, or only apply the
                            tailored behavior when the extortion response has no modified memory (meaning the response will be suspended otherwise). The default setting is to not limit.`}
                            tooltipPlacement={"bottom-start"}
                        />
                    </div>
                    <div className={`${additionalRestrictionsToggled ? "block ml-3" : "hidden"}`}>
                        <label>File Triggers To Match</label>
                        <MuiAutocompleteNonGroupOptions
                            options={fileTriggersToMatchAllOptions}
                            multiple={true}
                            label={"All Triggers Used by Default"}
                            value={incidentMatchTypeList}
                            onChange={(event, valueList) => {
                                setIncidentMatchTypeList(valueList)
                            }}
                        />
                    </div>
                    <div className={`flex flex-row items-center ml-1`}>
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={temporaryToggled}
                                    onChange={e => setTemporaryToggled(e.target.checked)}
                                />
                            }
                                              label={temporaryToggled ? "Make This Tailored Behavior Temporary" : "Make This Tailored Behavior Permanent"}/>
                        </ThemeProvider>
                    </div>
                    <div className={`${temporaryToggled ? "block ml-3" : "hidden"}`}>
                        <div className="flex flex-row items-center gap-x-3 gap-y-2 flex-wrap">
                            <label>Hours Until This Tailored Behavior Expires</label>
                            <input
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === "e" || e.key === "-" || e.key === "+" || e.key === ".") {
                                        e.preventDefault();
                                    }
                                }}
                                min={0} max={168} type="number"
                                autoComplete="off"
                                value={expirationHours}
                                onChange={(e) => setExpirationHours(e.target.value)}
                                className="text-center w-24 focus:outline-none h-10 p-1 rounded-lg border border-black border-opacity-25 border-solid"
                            />
                        </div>
                    </div>
                    <div className={`flex flex-row items-center ml-1`}>
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={hideCanariesToggled}
                                    onChange={e => setHideCanariesToggled(e.target.checked)}
                                />
                            }
                                              label={hideCanariesToggled ? "Deception data will be hidden" : "Deception data will be shown"}/>
                        </ThemeProvider>
                    </div>
                    <div className="flex flex-1 flex-col">
                        <ThemeProvider theme={buttonTheme}>
                            <Button variant={"contained"}
                                    color={"primary"}
                                    disabled={submitting}
                                    onClick={submit}
                            >
                                Create
                            </Button>
                        </ThemeProvider>
                    </div>
                </div>
            </div>

            {/* Stacked builder modals — write the generated pattern back into the targeted field */}
            <V2PathBuilderModal
                isOpen={builderTarget === 'path' || builderTarget === 'parentPath'}
                onClose={() => setBuilderTarget(null)}
                fieldLabel={builderTarget === 'parentPath' ? "Parent Program Path" : "Program Path"}
                initialPattern={builderTarget === 'parentPath' ? parentPath : path}
                onApply={(pattern) => {
                    if (builderTarget === 'parentPath') setParentPath(pattern);
                    else setPath(pattern);
                }}
            />
            <V2ArgsBuilderModal
                isOpen={builderTarget === 'programArguments' || builderTarget === 'parentProgramArguments'}
                onClose={() => setBuilderTarget(null)}
                fieldLabel={builderTarget === 'parentProgramArguments' ? "Parent Program Arguments" : "Program Arguments"}
                initialPattern={builderTarget === 'parentProgramArguments' ? parentProgramArguments : programArguments}
                onApply={(pattern) => {
                    if (builderTarget === 'parentProgramArguments') setParentProgramArguments(pattern);
                    else setProgramArguments(pattern);
                }}
            />
        </Modal>
    );
}